import {colors, Grid, IconButton, TextField, Theme} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import DeleteIcon from '@material-ui/icons/DeleteOutlined';
import React, {Dispatch, SetStateAction, useEffect} from 'react';
import {AttributeSchema, conditions, ConditionSchema, DataTypes, TypeSchema} from '../../../graphql/common.datatypes';
import {
  ResourceOption,
  ResourceOptionCondition,
  ResourceOptionConditionType,
  ResourceOptionConditionTypes
} from '../../../graphql/resources.datatypes';
import {getHandleFieldChange} from '../../../utils/formHelpers';
import {isValidDate} from '../../../utils/isValidDate';
import {numberRegEx, unixDateRegEx, usaDateRegEx} from '../../../utils/validators';
import {WCVDatePicker} from '../../../components/DateUtilsProvider/WCVDatePicker';
import {formatGraphQlDate} from '../../../utils/formatters';

const schema: { types: TypeSchema[] } = {
  types: [
    ResourceOptionConditionTypes[ResourceOptionConditionType.TRAVELLER],
    ResourceOptionConditionTypes[ResourceOptionConditionType.ROOM],
    ResourceOptionConditionTypes[ResourceOptionConditionType.BOOKING],
    ResourceOptionConditionTypes[ResourceOptionConditionType.ROOM_PARTY_NIGHT],
    ResourceOptionConditionTypes[ResourceOptionConditionType.ADMIN],
  ]
};

export const getBlankResourceOptionCondition = (resourceOptionId: number) => {
  const type = ResourceOptionConditionTypes[ResourceOptionConditionType.TRAVELLER];
  const attribute = type.atts[0];
  const conditionSchema = ({...conditions.find(item => item.type === attribute.type)} as ConditionSchema);
  const condition = conditionSchema.conditions[0].value;

  return {
    __typename: 'ResourceOptionCondition',
    id: -1,
    resource_option_id: resourceOptionId,
    type: type.value,
    attribute: attribute.value,
    attribute_type: attribute.type,
    attribute_value: attribute.defaultValue,
    condition,
  };
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(3)
  },
  textFieldInput: {
    fontSize: 12,
    height: 'auto',
  },
  dateInput: {
    '& .MuiInputAdornment-root': {
      marginLeft: 0,
    },
  },
  deleteButton: {
    margin: theme.spacing(1),
    color: theme.palette.error.dark,
  },
  saveButton: {
    margin: theme.spacing(1),
    color: colors.green[300],
  },
  icon: {
    fontSize: '1.2rem',
  },
}));

const ResourceOptionConditionEdit = (props: {
  condition: ResourceOptionCondition,
  index: number,
  handleDelete: () => void,
  //setFormState: (handler: (oldState: ResourceOption) => void) => void,
  setFormState: Dispatch<SetStateAction<ResourceOption>>,
  disabled: boolean
}) => {
  const classes = useStyles();
  const {condition, index, disabled} = props;

  //1. Set form and error state
  const setFormState = (handler: (oldCondState: ResourceOptionCondition) => ResourceOptionCondition) => {
    props.setFormState((oldState: ResourceOption) => {
      const oldCondition = oldState.conditions![index];
      const newCondition = handler(oldCondition);
      const newConditions = [...oldState.conditions!];
      newConditions[index] = newCondition;
      return {...oldState, conditions: newConditions};
    });
  };

  const getTypeSchema = () => ({...schema.types.find(type => type.value === condition.type)} as TypeSchema);
  const getAttributeSchema = () => (getTypeSchema().atts.find(att => att.value === condition.attribute) as AttributeSchema);
  const getConditionSchema = () => ({...conditions.find(item => item.type === condition.attribute_type)} as ConditionSchema);
  const attributeSchema = getAttributeSchema();

  //2. Setup helper functions for form
  const handleFieldChange = getHandleFieldChange(setFormState);

  //3. Update state when condition is changed.
  useEffect(() => {
    const attribute = getAttributeSchema();
    if(!attribute) setFormState(oldState => ({...condition, attribute: getTypeSchema().atts[0].value}));
  }, [condition.type]);
  useEffect(() => {
    const attribute = getAttributeSchema();
    if (attribute) setFormState(oldState => ({...condition, attribute_type: attribute.type}));
  }, [condition.attribute]);
  useEffect(() => {
    const hasCondition = getConditionSchema().conditions.find(item => item.value === condition.condition);
    if (!hasCondition) setFormState(oldState => ({...condition, condition: getConditionSchema().conditions[0].value}));
    //Check current value is valid for all types
    let newValue: string = '';
    if (condition.attribute_type === DataTypes.BOOLEAN && (condition.attribute_value !== 'true' && condition.attribute_value !== 'false')) newValue = 'true';
    if (condition.attribute_type === DataTypes.NUMBER && !numberRegEx.test(String(condition.attribute_value!))) newValue = '0';
    if (condition.attribute_type === DataTypes.DATE && (!isValidDate(condition.attribute_value) && !usaDateRegEx.test(String(condition.attribute_value!)) && !unixDateRegEx.test(String(condition.attribute_value!)))) newValue = formatGraphQlDate(new Date())!;
    if (attributeSchema.options && !attributeSchema.options.some(item => item.value === condition.attribute_value)) newValue = attributeSchema.options[0].value;
    //set new value
    if (newValue !== '') setFormState(oldState => ({...condition, attribute_value: newValue}));
  }, [condition.attribute_type]);

  return (
    <Grid container spacing={1}>
      <Grid item xs={3}>
        <TextField
          fullWidth
          label="Target"
          name="type"
          onChange={handleFieldChange}
          select
          // eslint-disable-next-line react/jsx-sort-props
          SelectProps={{native: true}}
          value={String(condition.type)}
          InputLabelProps={{
            shrink: true,
          }}
          inputProps={{
            className: classes.textFieldInput,
          }}
          disabled={disabled}
        >
          {schema.types.map((type) => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </TextField>
      </Grid>

      <Grid item xs={3}>
        <TextField
          fullWidth
          label="Attribute"
          name="attribute"
          onChange={handleFieldChange}
          select
          // eslint-disable-next-line react/jsx-sort-props
          SelectProps={{native: true}}
          value={String(condition.attribute)}
          InputLabelProps={{
            shrink: true,
          }}
          inputProps={{
            className: classes.textFieldInput,
          }}
          disabled={disabled}
        >
          {getTypeSchema().atts.map((attribute) => (
            <option key={attribute.value} value={attribute.value}>
              {attribute.label}
            </option>
          ))}
        </TextField>
      </Grid>

      <Grid item xs={2}>
        <TextField
          fullWidth
          label="Condition"
          name="condition"
          onChange={handleFieldChange}
          select
          // eslint-disable-next-line react/jsx-sort-props
          SelectProps={{native: true}}
          value={String(condition.condition)}
          InputLabelProps={{
            shrink: true,
          }}
          inputProps={{
            className: classes.textFieldInput,
          }}
          disabled={disabled}
        >
          {getConditionSchema().conditions.map((item: any) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </TextField>
      </Grid>

      <Grid item xs={3}>

        {attributeSchema && attributeSchema.options && (
          <TextField
            fullWidth
            label="Value"
            name="attribute_value"
            onChange={handleFieldChange}
            select
            // eslint-disable-next-line react/jsx-sort-props
            SelectProps={{native: true}}
            value={String(condition.attribute_value)}
            InputLabelProps={{
              shrink: true,
            }}
            inputProps={{
              className: classes.textFieldInput,
            }}
            disabled={disabled}
          >
            {getAttributeSchema().options!.map((item: any) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </TextField>
        )}

        {attributeSchema && !attributeSchema.options && attributeSchema.type === DataTypes.STRING && (
          <TextField
            fullWidth
            label="Value"
            name="attribute_value"
            onChange={handleFieldChange}
            value={String(condition.attribute_value)}
            InputLabelProps={{
              shrink: true,
            }}
            inputProps={{
              className: classes.textFieldInput,
            }}
            disabled={disabled}
          />
        )}

        {attributeSchema && !attributeSchema.options && attributeSchema.type === DataTypes.BOOLEAN && (
          <TextField
            fullWidth
            label="Value"
            name="attribute_value"
            onChange={handleFieldChange}
            select
            // eslint-disable-next-line react/jsx-sort-props
            SelectProps={{native: true}}
            value={String(condition.attribute_value)}
            InputLabelProps={{
              shrink: true,
            }}
            inputProps={{
              className: classes.textFieldInput,
            }}
            disabled={disabled}
          >
            <option value="true">true</option>
            <option value="false">false</option>
          </TextField>
        )}

        {attributeSchema && !attributeSchema.options && attributeSchema.type === DataTypes.NUMBER && (
          <TextField
            fullWidth
            label="Value"
            name="attribute_value"
            type="number"
            onChange={handleFieldChange}
            value={String(condition.attribute_value)}
            InputLabelProps={{
              shrink: true,
            }}
            inputProps={{
              className: classes.textFieldInput,
            }}
            disabled={disabled}
          />
        )}

        {attributeSchema && !attributeSchema.options && attributeSchema.type === DataTypes.DATE && (
            <WCVDatePicker
                margin="none"
                name="attribute_value"
                label="Value:"
                value={condition.attribute_value}
                onChange={(date, value) => setFormState(oldState => ({
                  ...oldState,
                  attribute_value: value ? value : undefined
                }))}
                className={classes.dateInput}
                inputProps={{
                  className: classes.textFieldInput,
                }}
                disabled={disabled}
            />
        )}

      </Grid>

      <Grid item xs={1}>
        <IconButton className={classes.deleteButton} onClick={props.handleDelete} disabled={disabled}>
          <DeleteIcon className={classes.icon}/>
        </IconButton>
      </Grid>
    </Grid>

  );
};

export default ResourceOptionConditionEdit;